import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountStatus,
  AccountTypeTextes,
  Box,
  CardTypesEnum,
  CIPStatus,
  CipStatusCard,
  Enrollment,
  EnrollmentStatus,
  SpendPeriodType,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import useFailedCIPStorage from '@/modules/investments/cip/hooks/useFailedCIPStorage';
import EnrollmentItem, {
  ENROLLMENT_ITEM_WIDTH_L,
} from '@/modules/transaction/components/EnrollmentItem/EnrollmentItem';
import { useExpensesStatisticsQuery } from '@/modules/transaction/hooks/useExpensesStatistics.query';
import { useAuthStore } from '@/modules/user';
import globalTheme from '@/styles/theme';

import { ContributionAchievementsTracker } from '../Contributions/ContributionAchievementsTracker';
import { EnrollmentDates } from '../EnrollmentItem/EnrollmentDates';
import ExpensesBreakdown from '../ExpensesBreakdown';

import { Header, HeaderWrapper } from './AccountDetails.styles';
import BeneficiaryInfo from './BeneficiaryInfo';
import EligibleServicesLine from './EligibleServicesLine';
import SpendPeriodTracker from './SpendPeriodTracker';

const expensesBreakdownHight = '244px';

const TYPES = ['medical', 'dental', 'pharmacy', 'vision', 'other'];
export const fsaExpenses = TYPES.map((item) => ({
  key: item,
  title: _.capitalize(item),
  value: Math.floor(Math.random() * 1000),
}));

export const getEnrollmentStatusColor = (status?: EnrollmentStatus) => {
  switch (status) {
    case EnrollmentStatus.Active: {
      return globalTheme.colors.statusColors.paidActiveComplete;
    }
    case EnrollmentStatus.Canceled: {
      return globalTheme.colors.statusColors.deniedErrorExpired;
    }
    case EnrollmentStatus.Future: {
      return globalTheme.colors.statusColors.future;
    }
    case EnrollmentStatus.Expired: {
      return globalTheme.colors.statusColors.deniedErrorExpired;
    }
    default: {
      return globalTheme.colors.statusColors.lockedNull;
    }
  }
};
interface Props {
  id: string;
  currentMyAccount: Enrollment;
}

const AccountDetails: React.FC<Props> = ({ id, currentMyAccount }) => {
  const { t } = useTranslation();

  const { formatedData } = useExpensesStatisticsQuery({
    account_id: Number(id),
  });
  const { isFailed: isVerificationFailed } = useFailedCIPStorage();
  const statusColor = useMemo(
    () => getEnrollmentStatusColor(currentMyAccount.enrollmentStatus),
    [currentMyAccount.enrollmentStatus]
  );
  const showSpendPeriodTracker = useMemo(
    () =>
      currentMyAccount?.spendPeriodType === SpendPeriodType.MONTHLY ||
      currentMyAccount?.spendPeriodType === SpendPeriodType.PLAN_YEAR,
    [currentMyAccount]
  );

  const { isMobile } = useResponsive();

  const content = useMemo(() => {
    if (currentMyAccount) {
      const { CIPStatus: currentCIPStatus, accountStatus, accountType } = currentMyAccount;
      if (accountStatus === AccountStatus.PENDING) {
        if (currentCIPStatus === CIPStatus.Pending || currentCIPStatus === CIPStatus.None) {
          return (
            <Box flex="grow">
              <CipStatusCard
                status="NEED_VERIFY"
                height={expensesBreakdownHight}
                text={
                  <>
                    <Text color="textBody" size="large" textAlign="center">
                      {t('We are verifying your identity to establish your account.')}
                    </Text>
                    <Text color="textBody" size="large" textAlign="center">
                      {t('This may take a few days')}
                    </Text>
                  </>
                }
              />
            </Box>
          );
        }
        if (
          (currentCIPStatus === CIPStatus.Fail || currentCIPStatus === CIPStatus.Question) &&
          isVerificationFailed
        ) {
          return (
            <div>
              <Box flex="grow">
                <CipStatusCard
                  status=""
                  height={expensesBreakdownHight}
                  text={
                    <Text color="textBody" size="large" textAlign="center">
                      {t(`We're sorry but we cannot open
                      an ${
                        AccountTypeTextes[accountType as keyof typeof AccountTypeTextes]
                      } for you as we were not able to successfully verify your identity.
                      Please contact your administrator for next steps.`)}
                    </Text>
                  }
                />
              </Box>
            </div>
          );
        }
      }
      if (currentMyAccount.contributionMonitoringRequired) {
        return (
          <Box flex="grow">
            <ContributionAchievementsTracker currentAccount={currentMyAccount} />
          </Box>
        );
      }
      return (
        <Box flex="grow" height={{ max: expensesBreakdownHight }}>
          {showSpendPeriodTracker ? (
            <SpendPeriodTracker
              accountId={currentMyAccount.id}
              spendPeriodType={currentMyAccount.spendPeriodType}
            />
          ) : (
            <ExpensesBreakdown
              options={formatedData?.expenses || []}
              totalExpense={formatedData?.totalExpenses || 0}
              inlineMode
              isWithoutHeader
            />
          )}
        </Box>
      );
    }
  }, [
    currentMyAccount,
    showSpendPeriodTracker,
    formatedData?.expenses,
    formatedData?.totalExpenses,
    isVerificationFailed,
    t,
  ]);

  const { user } = useAuthStore();
  const isUserTerminated = useMemo(
    () => user?.terminationDate && dayjs(user.terminationDate).isBefore(),
    [user]
  );

  return (
    <Box
      data-testid="AccountDetails-wrapper"
      round="moduleRound"
      background="module"
      pad="l"
      margin={{ vertical: 'l' }}
    >
      <HeaderWrapper>
        <Header>{t('Account Details')}</Header>
      </HeaderWrapper>
      <Box direction={isMobile ? 'column' : 'row'} justify="center">
        {currentMyAccount && (
          <EnrollmentItem
            isDateless
            width={isMobile ? '100%' : undefined}
            enrollment={currentMyAccount}
            wrapperStyle={{
              marginRight: isMobile ? '' : globalTheme.spacings.s,
              width: isMobile ? 'calc(100% -5px)' : ENROLLMENT_ITEM_WIDTH_L,
              flexShrink: 0,
            }}
          />
        )}
        {/* <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}> */}
        <Box margin={{ top: isMobile ? 's' : '' }} fill={!isMobile}>
          {content}
        </Box>
        {/* </Responsive> */}
      </Box>
      <EligibleServicesLine
        policyId={currentMyAccount?.policyId}
        accountName={currentMyAccount?.name}
        accountType={currentMyAccount?.accountType}
      />
      {currentMyAccount && (
        <EnrollmentDates
          isInlineMode
          coverageDateStart={currentMyAccount.coverageDateStart}
          submitClaimsDateEnd={currentMyAccount.submitClaimsDateEnd}
          coverageDateEnd={currentMyAccount.coverageDateEnd}
          enrollmentStatus={currentMyAccount.enrollmentStatus}
          coveragePeriods={currentMyAccount.coveragePeriods}
          externalDeductibleMet={currentMyAccount.deductibleMetDate}
          isRollover={currentMyAccount?.isRollover}
          electionPeriods={currentMyAccount?.electionPeriods}
          statusColor={statusColor}
        />
      )}

      {currentMyAccount?.accountType === CardTypesEnum.HSA && !isUserTerminated ? <BeneficiaryInfo /> : null}
    </Box>
  );
};

export default AccountDetails;
